import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subscription, take } from 'rxjs';
import { ExclusiveDropdownService } from '../../services/exclusive-dropdown.service';

@Component({
  selector: 'app-generic-autocomplete',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './generic-autocomplete.component.html',
  styleUrl: './generic-autocomplete.component.css',
})
export class GenericAutocompleteComponent<T> implements OnInit, OnDestroy {
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() placeholder: string = '';
  @Input() control!: FormControl;
  @Input() searchFn!: (query: string) => Observable<T[]>;
  @Input() displayFn: (item: T) => string = (item: T) =>
    (item as { name?: string })?.name || String(item);
  @Input() id: string = 'autocomplete-' + Math.random().toString(36).substr(2, 9);
  @Input() minSearchLength: number = 2;
  @Input() showAllOnEmpty: boolean = false;
  @Input() debounceMs: number = 1000;
  @Output() itemSelected: EventEmitter<T> = new EventEmitter<T>();
  @Output() queryChanged: EventEmitter<string> = new EventEmitter<string>();

  results: T[] = [];
  loading: boolean = false;
  showDropdown: boolean = false;
  private sub: Subscription = new Subscription();
  searchInput = new FormControl('');
  private readonly exclusiveDropdown = inject(ExclusiveDropdownService);
  private hasFocus = false;

  ngOnInit() {
    if (this.control.value) {
      this.searchInput.setValue(this.formatControlValue(this.control.value));
    }

    this.sub.add(
      this.control.valueChanges.subscribe((value) => {
        const displayText = this.formatControlValue(value);
        if (this.searchInput.value !== displayText) {
          this.searchInput.setValue(displayText, { emitEvent: false });
        }
      }),
    );

    this.sub.add(
      this.searchInput.valueChanges
        .pipe(
          debounceTime(this.debounceMs),
          distinctUntilChanged(),
          tap((query) => {
            const normalizedQuery = typeof query === 'string' ? query.trim() : '';
            this.queryChanged.emit(normalizedQuery);
            const nextValue = (normalizedQuery.length > 0 ? normalizedQuery : null) as T | null;
            if (this.control.value !== nextValue) {
              this.control.setValue(nextValue);
            }
          }),
          tap(() => {
            if (!this.disabled && this.hasFocus) {
              this.loading = true;
              this.openDropdown();
            }
          }),
          switchMap((query) => {
            if (this.disabled) {
              return of([]);
            }
            const normalizedQuery = typeof query === 'string' ? query.trim() : '';
            if (this.showAllOnEmpty && normalizedQuery.length === 0) {
              return this.searchFn('');
            }
            if (normalizedQuery.length < this.minSearchLength) {
              return of([]);
            }
            return this.searchFn(normalizedQuery);
          }),
        )
        .subscribe({
          next: (results) => {
            this.results = results;
            this.loading = false;
          },
          error: () => {
            this.results = [];
            this.loading = false;
          },
        }),
    );
  }

  selectItem(item: T) {
    this.control.setValue(item);
    this.searchInput.setValue(this.displayFn(item), { emitEvent: false });
    this.closeDropdown();
    this.itemSelected.emit(item);
  }

  closeDropdown(): void {
    this.showDropdown = false;
    this.exclusiveDropdown.closed(this);
  }

  onBlur() {
    if (this.disabled) {
      return;
    }
    this.hasFocus = false;
    setTimeout(() => {
      if (this.hasFocus) {
        return;
      }
      this.closeDropdown();
      if (!this.control.value) {
        this.searchInput.setValue('', { emitEvent: false });
      } else {
        const currentDisplay = this.formatControlValue(this.control.value);
        if (this.searchInput.value !== currentDisplay) {
          this.searchInput.setValue(currentDisplay, { emitEvent: false });
        }
      }
    }, 200);
  }

  onFocus() {
    if (this.disabled) {
      return;
    }
    this.hasFocus = true;
    this.openDropdown();
    if (this.control.value) {
      this.searchInput.setValue('');
      this.control.setValue(null);
    }

    if (this.showAllOnEmpty && !this.searchInput.value) {
      this.loading = true;
      this.searchFn('')
        .pipe(take(1))
        .subscribe({
          next: (results) => {
            this.results = results;
            this.loading = false;
          },
          error: () => {
            this.results = [];
            this.loading = false;
          },
        });
      return;
    }

    if (this.searchInput.value && this.searchInput.value.length >= this.minSearchLength) {
      this.openDropdown();
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.closeDropdown();
  }

  private openDropdown(): void {
    this.showDropdown = true;
    this.exclusiveDropdown.open(this);
  }

  /**
   * While typing, the control is set to the raw query string (used by list filters).
   * displayFn is only for selected items; applying it to a string yields "undefined (...)".
   */
  private formatControlValue(value: T | string | null): string {
    if (value == null || value === '') {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return this.displayFn(value);
  }
}
